import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Api } from '../api'
import { ApiError } from '../api/types'
import type {
  ApiFailure, StatementCompany, StatementCustomer, StatementHeader, StatementKind, StatementResponse,
} from '../api/types'
import { DevModeProvider } from '../components/MeasurePanel'
import { Explain } from '../components/DevModeContext'
import { FailureState } from '../components/FailureState'
import {
  Button, Card, EmptyState, FloatingPanel, Loading, MultiSearchSelect, NAME_PANEL_WIDTH, Notice, PageTabs, ReportHeader,
  Segmented, inputClass, useFloatingPanel, type SearchSelectOption,
} from '../components/ui'
import {
  DASH,
  addressLines,
  amountsWithheld,
  closingNote,
  closingsOf,
  columnsFor,
  companiesOf,
  contacts,
  creditUse,
  currenciesOf,
  docDate,
  isoDate,
  kindLabel,
  money,
  openNote,
  openingNote,
  periodText,
  preparedAt,
  sideAmount,
  type CompanyStatement,
  type StatementDoc,
} from '../export/statementModel'

/**
 * STATEMENT OF ACCOUNT — a customer's statement to send to the customer, as the
 * D365 team specified it on 6 Oct 2026 (version 2; artifacts/soa/PLAN.md has
 * every decision and measurement):
 *   - FULL lists every transaction in the period after an opening balance of
 *     everything before it, D365's matching pairs included; OPEN lists what is
 *     unpaid TODAY among the lines dated up to To, unmatched payments as
 *     credits -- the data keeps no history of payment matching, so "unpaid on
 *     a past date" cannot be rebuilt, and the page says "as of today";
 *   - the customer is D365's customer RECORD, which can hold an account in
 *     several companies: the reader ticks which, and each company prints as a
 *     statement of its own, with its own letterhead, terms and balances;
 *   - one section per transaction currency, the company's own first, and no
 *     currency switch: a statement goes in what the customer was billed in.
 *
 * D365'S DATA IN THE PLATFORM'S DESIGN (version 3, the owner's request of 6 Oct
 * 2026). Everything D365's own account statement prints except the tax number:
 * the company's address, telephone and fax; the customer, their address and
 * account; terms, currency, period, credit limit and credit available; when the
 * copy was made. Laid out as a sheet of paper -- letterhead, then Bill to,
 * Account and the closing balance side by side, then the ledger -- in the MBI
 * extract's look and IBM Plex, the face the PDF is set in (styles.css says why
 * that is the product's one web font). The ledger's columns are the D365 team's
 * templates': Date, Salesperson, Document, Sales order, Reference, Due date,
 * Days overdue, [Original amount,] Debit, Credit and Balance.
 *
 * A DOCUMENT, NOT A GRID. Its columns are the ones the customer receives, in
 * that order, and they do not sort: a running balance read in any order but the
 * ledger's is a column of wrong numbers. So there is no column menu and no
 * saved view -- TAB_GRIDS in viewScope.ts says the same.
 */

type TabId = 'statement'

const TABS: { id: TabId; label: string; title: string }[] = [
  { id: 'statement', label: 'Statement', title: "A customer's statement for a period, as it will be sent to them" },
]

const KINDS: { value: StatementKind; label: string; title: string }[] = [
  { value: 'full', label: 'Full statement', title: 'Every transaction in the period, after the balance carried in' },
  { value: 'open', label: 'Open items', title: 'What is unpaid today, among the lines dated up to the To date' },
]

/** The picker's panel apart from its list: the search row and the footer line under the list. */
const PICKER_HEAD = 96

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** One customer record, as the search groups its rows: one entry, its visible accounts. */
interface CustomerRecord {
  party: string
  name: string
  accounts: StatementCustomer[]
}

/** The search's rows as records, in the order the server ranked them. */
function recordsOf(rows: StatementCustomer[]): CustomerRecord[] {
  const out: CustomerRecord[] = []
  const at = new Map<string, CustomerRecord>()
  for (const r of rows) {
    if (!r.party || !r.company || !r.customerAccount) continue
    let rec = at.get(r.party)
    if (!rec) {
      rec = { party: r.party, name: r.customerName ?? r.customerAccount, accounts: [] }
      at.set(r.party, rec)
      out.push(rec)
    }
    rec.accounts.push(r)
  }
  return out
}

/**
 * The default period as the BROWSER sees it, for the date boxes before the
 * first statement arrives. Only ever shown, never sent: until the reader
 * changes a date, the server picks the period itself (see api.statement).
 */
function localDefault(): { from: string; to: string } {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return { from: `${d.getFullYear()}-01-01`, to: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` }
}

export function StatementPage({ api, viewAs, roles = [] }: {
  api: Api
  viewAs: string | null
  /** For developer mode. Affordance only — the API refuses the panel to anyone who is not a System Admin. */
  roles?: string[]
}) {
  const [tab, setTab] = useState<TabId>('statement')
  const [record, setRecord] = useState<CustomerRecord | null>(null)
  /* THE COMPANY BOX'S TICKS, as codes (the owner's choice, 6 Oct 2026: any mix).
     EMPTY MEANS ALL -- every company the reader can see while no customer is
     chosen, and every one of the customer's after: MultiSearchSelect has no
     "nothing ticked" state, so neither does the statement. */
  const [companies, setCompanies] = useState<string[]>([])
  /* The companies the reader can see a customer in; null while unknown. */
  const [companyList, setCompanyList] = useState<StatementCompany[] | null>(null)
  const [kind, setKind] = useState<StatementKind>('full')
  /* null IS THE DEFAULT PERIOD, and the server decides it: 1 January to the
     server's own today. */
  const [period, setPeriod] = useState<{ from: string; to: string } | null>(null)
  const [first, setFirst] = useState<StatementCustomer[] | null>(null)
  const [data, setData] = useState<StatementResponse | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  /* The report refusing this reader at all: the page shows that, not a picker. */
  const [refusal, setRefusal] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)
  const [retry, setRetry] = useState(0)
  const [downloading, setDownloading] = useState<'pdf' | 'xlsx' | null>(null)
  const [downloadFailed, setDownloadFailed] = useState(false)

  /* THE SHEET'S TYPEFACE, asked for on arrival, so it has loaded by the time a
     statement has (styles.css): the sheet then arrives set instead of
     reflowing. Best effort -- the sheet falls back to the platform's font. */
  useEffect(() => {
    const faces = typeof document !== 'undefined' ? document.fonts : undefined
    if (!faces?.load) return
    void Promise.all(['400', '600', '700'].map((w) => faces.load(`${w} 1em "IBM Plex Sans"`))).catch(() => undefined)
  }, [])

  // A different pair of eyes sees different customers and companies: start again.
  useEffect(() => {
    setRecord(null)
    setCompanies([])
    setPeriod(null)
    setData(null)
    setFailure(null)
  }, [viewAs])

  /* THE COMPANIES THE READER CAN SEE, asked for on arrival. Somebody who can
     see only one starts on it; everybody else on all of theirs (the owner's
     choice, 6 Oct 2026). A refusal is the report's, shown on the page. */
  useEffect(() => {
    let cancelled = false
    setCompanyList(null)
    api.statementCompanies()
      .then((r) => {
        if (cancelled) return
        setCompanyList(r)
        if (r.length === 1 && r[0].company) setCompanies([r[0].company])
      })
      .catch((e: unknown) => {
        if (!cancelled && e instanceof ApiError && e.failure.kind !== 'error') setRefusal(e.failure)
      })
    return () => { cancelled = true }
  }, [api, viewAs, retry])

  const companiesKey = companies.join(',')

  /* THE FIRST PAGE OF CUSTOMERS, in the companies ticked: what the picker
     opens on, asked again whenever the ticks change. It is also how a reader
     the report refuses finds out at once, on the page, rather than inside a
     dropdown. An error that is not a refusal leaves it unknown, and the picker
     asks again when it opens. */
  useEffect(() => {
    let cancelled = false
    setFirst(null)
    setRefusal(null)
    api.statementCustomers('', companiesKey ? companiesKey.split(',') : [])
      .then((r) => { if (!cancelled) setFirst(r) })
      .catch((e: unknown) => {
        if (!cancelled && e instanceof ApiError && e.failure.kind !== 'error') setRefusal(e.failure)
      })
    return () => { cancelled = true }
  }, [api, viewAs, retry, companiesKey])

  const head = data?.headers[0] ?? null
  const shown = period ?? (head ? { from: isoDate(head.fromDate), to: isoDate(head.toDate) } : localDefault())
  const periodProblem = period === null
    ? null
    : !ISO_DAY.test(period.from) || !ISO_DAY.test(period.to)
      ? 'Choose both dates.'
      : period.from > period.to
        ? 'The From date is after the To date.'
        : null
  /* The companies the statement covers: those ticked, or all of the customer's. */
  const statementKey = record
    ? (companies.length > 0 ? companies : record.accounts.map((a) => a.company as string)).join(',')
    : ''

  /* THE STATEMENT, whenever the record, the companies, the kind or the period
     changes. Not on a Login As change: that clears the record above, which is
     what re-runs this. */
  useEffect(() => {
    if (!record || statementKey === '' || periodProblem) {
      setData(null)
      setFailure(null)
      setBusy(false)
      return
    }
    let cancelled = false
    setBusy(true)
    setFailure(null)
    api.statement(record.party, statementKey.split(','), kind, period?.from, period?.to)
      // A statement is a document for the customer: whole or not at all. Under the new
      // model too, one withheld amount behind it -- a blank balance, or a line's
      // Partial -- shows every amount as a dash and says not to send it
      // (amountsWithheld), as the PDF and the workbook do (M8).
      .then((r) => { if (!cancelled) setData(r.data) })
      .catch((e: unknown) => {
        if (cancelled) return
        setData(null)
        setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not load the statement.' })
      })
      .finally(() => { if (!cancelled) setBusy(false) })
    return () => { cancelled = true }
  }, [api, record, statementKey, kind, period, periodProblem, retry])

  /* A customer chosen: the ticks keep only the customer's own companies, and
     nothing ticked stays "all of theirs". */
  const choose = (rec: CustomerRecord) => {
    const own = new Set(rec.accounts.map((a) => a.company as string))
    setRecord(rec)
    setCompanies((prev) => prev.filter((c) => own.has(c)))
  }
  /* Back to choosing: the Company box offers every company again, ticks kept. */
  const clearCustomer = () => {
    setRecord(null)
    setData(null)
    setFailure(null)
  }

  /* THE COMPANY BOX'S OPTIONS. Before a customer: every company the reader can
     see a customer in, with how many. After: that customer's companies, with
     its account number in each -- the "vice versa" of the request. */
  const companyOptions: SearchSelectOption[] = record
    ? record.accounts.map((a) => ({
        value: a.company as string,
        label: a.companyName ?? (a.company as string),
        hint: `${a.company} · ${a.customerAccount}`,
      }))
    : (companyList ?? []).flatMap((c) => (c.company ? [{
        value: c.company,
        label: c.companyName ?? c.company,
        hint: `${c.company} · ${c.customers.toLocaleString()} ${c.customers === 1 ? 'customer' : 'customers'}`,
      }] : []))

  const download = async (fileKind: 'pdf' | 'xlsx') => {
    if (!data) return
    setDownloading(fileKind)
    setDownloadFailed(false)
    const doc: StatementDoc = { headers: data.headers, lines: data.lines }
    try {
      if (fileKind === 'pdf') {
        const { exportStatementPdf } = await import('../export/statementPdf')
        await exportStatementPdf(doc)
      } else {
        const { exportStatementExcel } = await import('../export/statementExcel')
        await exportStatementExcel(doc)
      }
    } catch {
      setDownloadFailed(true)
    } finally {
      setDownloading(null)
    }
  }

  const active = TABS.find((t) => t.id === tab) ?? TABS[0]
  const ready = Boolean(data) && !busy && !failure

  return (
    <DevModeProvider
      api={api}
      roles={roles}
      report="statement-of-account"
      request={{
        Screen: tab,
        Statement: kind,
        Companies: record ? statementKey : companiesKey || 'all',
        ...(record ? { Customer: `${record.name} (${record.party})` } : {}),
        Period: period ? `${period.from} to ${period.to}` : '1 January to today',
      }}
    >
    <div className="stack-tight flex h-full flex-col gap-4">
      <ReportHeader
        report="Statement of Account"
        screen={active.label}
        what="A customer's statement for a period, to send to the customer."
        viewAs={viewAs}
        right={refusal ? undefined : (
          <DownloadButtons ready={ready} busy={downloading} failed={downloadFailed} onDownload={(k) => void download(k)} />
        )}
      />

      <PageTabs tabs={TABS} value={tab} onChange={setTab} label="Statement of Account pages" />

      {refusal ? <FailureState failure={refusal} onRetry={() => setRetry((n) => n + 1)} /> : (
      <>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-0 flex-col gap-1 @max-3xl:w-full">
          <Explain surface="filters" measureKey="companies" className="eyebrow">
            Company
          </Explain>
          <MultiSearchSelect
            label="Company"
            noun="companies"
            options={companyOptions}
            value={companies}
            onChange={setCompanies}
            disabled={!record && companyList === null}
            buttonClassName="w-[15rem] @max-3xl:w-full"
            panelWidth={NAME_PANEL_WIDTH}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1 @max-3xl:w-full">
          <Explain surface="filters" measureKey="customer" className="eyebrow">
            Customer
          </Explain>
          <CustomerPicker
            api={api}
            value={record}
            onChange={choose}
            onClear={clearCustomer}
            companies={companies}
            first={first}
            onRefused={setRefusal}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="eyebrow">Statement</span>
          <Segmented options={KINDS} value={kind} onChange={setKind} label="Statement" surface="filters" measureKey="kind" />
        </div>
        <div className="flex flex-col gap-1">
          <Explain surface="filters" measureKey="period" className="eyebrow">
            Period
          </Explain>
          <div className="flex items-center gap-2">
            <input
              type="date"
              aria-label="From"
              className={`${inputClass} w-[10.5rem]`}
              value={shown.from}
              max={shown.to || undefined}
              onChange={(e) => setPeriod({ from: e.target.value, to: shown.to })}
            />
            <span className="text-(length:--text-small) text-(--color-ink-3)">to</span>
            <input
              type="date"
              aria-label="To"
              className={`${inputClass} w-[10.5rem]`}
              value={shown.to}
              min={shown.from || undefined}
              onChange={(e) => setPeriod({ from: shown.from, to: e.target.value })}
            />
          </div>
        </div>
        {period ? (
          <Button variant="ghost" onClick={() => setPeriod(null)}>
            1 January to today
          </Button>
        ) : null}
      </div>

      {periodProblem ? (
        <p role="alert" className="text-(length:--text-small) text-(--color-overdue)">{periodProblem}</p>
      ) : null}

      {!record ? (
        <Card className="min-h-0 flex-1">
          <EmptyState
            title="Choose a customer"
            detail="Tick a company to see only its customers, or search them all by account number or name. Once a customer is chosen, the Company box lists that customer's companies: tick the ones the statement should cover, or leave it on all of them. Full statement lists every transaction; Open items, what is still unpaid. The period runs from 1 January to today unless you change the dates."
          />
        </Card>
      ) : periodProblem ? null
        : failure?.kind === 'not-found' ? (
          <Card className="min-h-0 flex-1">
            <EmptyState
              title="No statement for this customer."
              detail="The customer is not among the accounts you can see in the companies chosen, or does not exist."
            />
          </Card>
        ) : failure ? <FailureState failure={failure} onRetry={() => setRetry((n) => n + 1)} />
        : busy || !data ? <Loading label="Preparing the statement…" />
        : <StatementDocument data={data} />}
      </>
      )}
    </div>
    </DevModeProvider>
  )
}

/* ---------------------------------------------------------- customer picker */

/**
 * THE CUSTOMER SEARCH. Shaped and placed exactly like every picker on the
 * platform (SearchSelect), with two differences that are the reason it is not
 * one: a reader can see thousands of customers, so the SERVER does the
 * searching, by account number or name, Arabic included; and an entry is a
 * D365 customer RECORD, listing every company in which the reader can see its
 * account.
 */
function CustomerPicker({ api, value, onChange, onClear, companies, first, onRefused }: {
  api: Api
  value: CustomerRecord | null
  onChange: (r: CustomerRecord) => void
  /** Back to no customer: the Company box offers every company again. */
  onClear: () => void
  /** The Company box's ticks: each search is narrowed to them. Empty is all. */
  companies: string[]
  /** The first page, fetched on arrival; null while unknown. */
  first: StatementCustomer[] | null
  /** A refusal is the report's, and goes to the page. */
  onRefused: (f: ApiFailure) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const companiesKey = companies.join(',')
  const [results, setResults] = useState<StatementCustomer[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState(false)
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const id = useId()
  const place = useFloatingPanel(open, rootRef, NAME_PANEL_WIDTH, PICKER_HEAD)
  const placed = place !== null

  /* Asked on open and on each pause in the typing. An empty search on open is
     answered from the first page the page already holds. */
  useEffect(() => {
    if (!open) return
    const term = query.trim()
    if (!term && first) {
      setResults(first)
      setError(false)
      setSearching(false)
      return
    }
    let cancelled = false
    const timer = window.setTimeout(() => {
      setSearching(true)
      setError(false)
      api.statementCustomers(term, companiesKey ? companiesKey.split(',') : [])
        .then((r) => { if (!cancelled) setResults(r) })
        .catch((e: unknown) => {
          if (cancelled) return
          if (e instanceof ApiError && e.failure.kind !== 'error' && e.failure.kind !== 'not-found') {
            setOpen(false)
            onRefused(e.failure)
          } else setError(true)
        })
        .finally(() => { if (!cancelled) setSearching(false) })
    }, term ? 250 : 0)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [api, open, query, first, onRefused, companiesKey])

  const list = recordsOf(results ?? [])
  const total = results?.[0]?.totalMatches ?? 0

  // The arrows survive a list that changes under them.
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, list.length - 1)))
  }, [list.length])

  useEffect(() => {
    if (open && placed) inputRef.current?.focus({ preventScroll: true })
  }, [open, placed])

  useEffect(() => {
    if (!open || !placed) return
    listRef.current?.querySelector(`#${CSS.escape(`${id}-opt-${active}`)}`)?.scrollIntoView({ block: 'nearest' })
  }, [open, placed, active, id])

  /* Anywhere else closes it: outside the control AND outside its panel, which
     lives at the end of the document (see SearchSelect). */
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (!rootRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  const openPanel = () => {
    setQuery('')
    setActive(0)
    setOpen(true)
  }
  const close = (returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) buttonRef.current?.focus()
  }
  const choose = (r: CustomerRecord) => {
    onChange(r)
    close(true)
  }

  const onKeys = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close(true)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => (list.length === 0 ? 0 : (a + 1) % list.length))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => (list.length === 0 ? 0 : (a - 1 + list.length) % list.length))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (list[active]) choose(list[active])
    } else if (e.key === 'Tab') {
      buttonRef.current?.focus({ preventScroll: true })
      setOpen(false)
    }
  }

  const where = (r: CustomerRecord) =>
    r.accounts.map((a) => `${a.customerAccount} · ${a.companyName ?? a.company}`).join('   ')

  const term = query.trim()
  const footer = error
    ? 'Could not search the customers. Type again to retry.'
    : searching
      ? 'Searching…'
      : results !== null && list.length === 0
        ? term ? `No customer matches “${term}” among the accounts you can see.` : 'There are no customers among the accounts you can see.'
        : total > list.length
          ? `Showing ${list.length} of ${total.toLocaleString()}. Type more of the name or account to narrow it.`
          : null

  return (
    <div ref={rootRef} className="relative w-[26rem] max-w-full @max-3xl:w-full">
      <button
        ref={buttonRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={`${id}-list`}
        aria-label="Customer"
        onClick={() => (open ? close(false) : openPanel())}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            openPanel()
          }
        }}
        className={`${inputClass} flex items-center justify-between gap-2 text-left`}
      >
        {value ? (
          <span className="flex min-w-0 items-baseline gap-2 pr-6" title={value.name}>
            <bdi className="truncate font-medium">{value.name}</bdi>
            <span className="shrink-0 text-(length:--text-small) text-(--color-ink-3)">
              {value.accounts.length > 1 ? `${value.accounts.length} companies` : where(value)}
            </span>
          </span>
        ) : (
          <span className="truncate text-(--color-ink-3)">Search by name or account number…</span>
        )}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden
          className={`shrink-0 text-(--color-ink-3) transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M1.5 3.5L5 7l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {/* Beside the trigger, not inside it: a button inside a button is not
          valid, and the trigger must stay one control for the keyboard. */}
      {value ? (
        <button
          type="button"
          aria-label="Clear the customer"
          title="Clear the customer"
          onClick={() => {
            onClear()
            buttonRef.current?.focus()
          }}
          className="absolute top-1/2 right-8 grid size-5 -translate-y-1/2 place-items-center rounded text-(--color-ink-3) hover:bg-(--color-surface-3) hover:text-(--color-ink)"
        >
          <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden>
            <path d="M2 2l6 6M8 2l-6 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      ) : null}

      {open && place && (
        <FloatingPanel place={place} panelRef={panelRef} onKeyDown={onKeys}>
          <div className="border-b border-(--color-line) p-2">
            <input
              ref={inputRef}
              type="text"
              role="searchbox"
              aria-label="Search customers"
              aria-activedescendant={list[active] ? `${id}-opt-${active}` : undefined}
              placeholder="Name or account number…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setActive(0)
              }}
              className="w-full rounded border border-transparent bg-(--color-surface-2) px-2.5 py-1.5 text-(length:--text-base) text-(--color-ink) outline-none placeholder:text-(--color-ink-3) focus:border-(--color-navy-400) focus:bg-(--color-surface)"
            />
          </div>
          <ul
            ref={listRef}
            id={`${id}-list`}
            role="listbox"
            aria-label="Customers"
            tabIndex={-1}
            style={{ maxHeight: place.listMax }}
            className={`scroll-slim overflow-y-auto p-1 outline-none ${searching ? 'opacity-60' : ''}`}
          >
            {list.map((r, i) => {
              const chosen = value !== null && value.party === r.party
              return (
                <li
                  key={r.party}
                  id={`${id}-opt-${i}`}
                  role="option"
                  aria-selected={chosen}
                  title={r.name}
                  onPointerMove={() => setActive(i)}
                  onClick={() => choose(r)}
                  className={`flex cursor-pointer flex-col rounded px-2.5 py-1.5 ${i === active ? 'bg-(--color-surface-2)' : ''}`}
                >
                  <bdi className={`truncate text-(length:--text-base) ${chosen ? 'font-semibold text-(--color-navy-700)' : 'text-(--color-ink)'}`}>
                    {r.name}
                  </bdi>
                  <span className="truncate text-(length:--text-small) text-(--color-ink-3)">{where(r)}</span>
                </li>
              )
            })}
          </ul>
          {footer ? (
            <div className="border-t border-(--color-line) px-3 py-2 text-(length:--text-small) text-(--color-ink-3)">{footer}</div>
          ) : null}
        </FloatingPanel>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- download */

function DownloadButtons({ ready, busy, failed, onDownload }: {
  ready: boolean
  busy: 'pdf' | 'xlsx' | null
  failed: boolean
  onDownload: (kind: 'pdf' | 'xlsx') => void
}) {
  // The extract pair's own look (ExportButtons), so the platform has one.
  const btn =
    'inline-flex items-center gap-1.5 rounded-md border border-(--color-line-strong) px-2.5 py-1.5 ' +
    'text-(length:--text-small) font-semibold text-(--color-ink-2) transition-colors ' +
    'hover:bg-(--color-surface-2) hover:text-(--color-ink) disabled:opacity-50'
  const hint = ready ? undefined : 'Choose a customer and a period first'
  return (
    <div className="flex items-center gap-2" role="group" aria-label="Download this statement">
      <span className="eyebrow">Download</span>
      <button
        type="button"
        className={btn}
        disabled={!ready || busy !== null}
        onClick={() => onDownload('xlsx')}
        title={hint ?? 'The statement as an Excel workbook, with real numbers and dates to reconcile against'}
      >
        <DownloadIcon />
        {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
      </button>
      <button
        type="button"
        className={btn}
        disabled={!ready || busy !== null}
        onClick={() => onDownload('pdf')}
        title={hint ?? 'The statement as a PDF to send to the customer'}
      >
        <DownloadIcon />
        {busy === 'pdf' ? 'Preparing…' : 'PDF'}
      </button>
      {failed ? (
        <span className="text-(length:--text-small) text-(--color-overdue)">The download failed. Try again.</span>
      ) : null}
    </div>
  )
}

function DownloadIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden>
      <path d="M6 1v6.4M3.4 5.2 6 7.8l2.6-2.6M1.6 10.4h8.8" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/* ---------------------------------------------------------------- document */

/**
 * THE STATEMENT AS IT WILL PRINT: one sheet per company, laid on a desk. Each
 * sheet is the PDF's page drawn in the page's own terms -- the same letterhead,
 * the same three panels, the same table -- so what a salesperson checks here is
 * what the customer receives.
 */
function StatementDocument({ data }: { data: StatementResponse }) {
  const withheld = amountsWithheld(data.lines)
  const companies = companiesOf({ headers: data.headers, lines: data.lines })
  // When this copy was made, as the PDF stamps its own: once per statement loaded.
  const prepared = useMemo(() => preparedAt(), [data])
  return (
    <div className="scroll-slim min-h-0 flex-1 overflow-auto rounded-lg border border-(--color-line) bg-(--color-surface-3)">
      <div className="mx-auto flex max-w-[80rem] flex-col gap-8 p-6 @max-3xl:gap-5 @max-3xl:p-2">
        {withheld ? (
          <Notice tone="warn" title="Amounts withheld">
            This report's column access hides the amounts from you, so they show as dashes. Do not send the
            statement as it is: ask an administrator to look at the report's column access.
          </Notice>
        ) : null}
        {companies.map((c) => (
          <CompanySheet key={c.header.company} company={c} withheld={withheld} prepared={prepared} />
        ))}
      </div>
    </div>
  )
}

/** The small-caps label every panel opens with; its colour is the panel's. */
const LABEL = 'text-(length:--text-micro) font-semibold tracking-[0.08em] uppercase'

/** One company's statement: letterhead, the three panels, the ledger. */
function CompanySheet({ company: c, withheld, prepared }: {
  company: CompanyStatement
  withheld: boolean
  prepared: string
}) {
  const h = c.header
  return (
    <article
      className="statement-sheet min-w-[66rem] overflow-hidden rounded-md bg-(--color-surface) shadow-(--shadow-raised) @max-3xl:min-w-0"
      aria-label={`Statement from ${h.companyName ?? h.company ?? ''}`}
    >
      <div aria-hidden className="h-1.5 bg-(--color-navy-700)" />
      <div className="flex flex-col gap-7 px-10 pt-8 pb-10 @max-3xl:gap-5 @max-3xl:px-4 @max-3xl:pt-5 @max-3xl:pb-6">
        <Letterhead h={h} prepared={prepared} />
        <div aria-hidden className="h-px bg-(--color-line)" />
        <div className="grid grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,0.95fr)] gap-8 @max-5xl:grid-cols-2 @max-3xl:grid-cols-1 @max-3xl:gap-5">
          <BillTo h={h} />
          <AccountFacts h={h} currencies={currenciesOf(c)} />
          <BalancePanel company={c} withheld={withheld} />
        </div>
        <Ledger company={c} withheld={withheld} />
      </div>
    </article>
  )
}

/** The company, its address and numbers; the document's name, its period and when it was made. */
function Letterhead({ h, prepared }: { h: StatementHeader; prepared: string }) {
  const address = addressLines(h.companyAddress)
  const phones = contacts(h)
  return (
    <header className="flex items-start justify-between gap-10 @max-3xl:flex-col @max-3xl:gap-5">
      <div className="min-w-0">
        <div className="text-[1.375rem] leading-tight font-bold tracking-[-0.012em] text-(--color-navy-800)">
          <bdi>{h.companyName ?? h.company}</bdi>
        </div>
        {address.length > 0 ? (
          <Explain surface="statement" measureKey="companyAddress" className="mt-2 block text-(length:--text-small) leading-[1.6] text-(--color-ink-2)">
            {address.map((line, i) => (
              <span key={i} className="block"><bdi>{line}</bdi></span>
            ))}
          </Explain>
        ) : null}
        {phones.length > 0 ? (
          <Explain surface="statement" measureKey="companyContact" className="mt-2 flex flex-wrap gap-x-5 text-(length:--text-small)">
            {phones.map((p) => (
              <span key={p.label}>
                <span className="font-semibold text-(--color-ink-3)">{p.label}</span>{' '}
                <span className="text-(--color-ink)">{p.value}</span>
              </span>
            ))}
          </Explain>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-end text-right @max-3xl:items-start @max-3xl:text-left">
        <div className={`${LABEL} text-(--color-navy-700)`}>Customer account statement</div>
        <div className="mt-1.5 text-[1.75rem] leading-none font-semibold tracking-[-0.02em] text-(--color-ink)">
          <Explain surface="filters" measureKey="kind">{kindLabel(h.kind)}</Explain>
        </div>
        {h.kind === 'open' ? (
          <div className="mt-2 rounded-full bg-(--color-navy-50) px-2.5 py-0.5 text-(length:--text-small) font-semibold text-(--color-navy-700)">
            {openNote(h)}
          </div>
        ) : null}
        <dl className="mt-4 grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-(length:--text-small)">
          <dt className="text-(--color-ink-3)">Period</dt>
          <dd className="m-0 font-semibold text-(--color-ink)">
            <Explain surface="filters" measureKey="period">{periodText(h)}</Explain>
          </dd>
          <dt className="text-(--color-ink-3)">Statement date</dt>
          <dd className="m-0 text-(--color-ink)">{prepared}</dd>
        </dl>
      </div>
    </header>
  )
}

/** Who the statement is addressed to. */
function BillTo({ h }: { h: StatementHeader }) {
  const address = addressLines(h.customerAddress)
  return (
    <section aria-label="Bill to" className="min-w-0">
      <div className={`${LABEL} text-(--color-ink-3)`}>Bill to</div>
      <Explain surface="statement" measureKey="customer" className="mt-2 block text-[1.0625rem] leading-snug font-semibold text-(--color-ink)">
        <bdi>{h.customerName ?? h.customerAccount}</bdi>
      </Explain>
      {address.length > 0 ? (
        <Explain surface="statement" measureKey="customerAddress" className="mt-1.5 block text-(length:--text-small) leading-[1.6] text-(--color-ink-2)">
          {address.map((line, i) => (
            <span key={i} className="block"><bdi>{line}</bdi></span>
          ))}
        </Explain>
      ) : null}
    </section>
  )
}

/** The account's standing: number, terms, currencies, and the credit position at To. */
function AccountFacts({ h, currencies }: { h: StatementHeader; currencies: string[] }) {
  const use = creditUse(h)
  const home = h.homeCurrency ?? ''
  const over = h.creditAvailable !== null && h.creditAvailable < 0
  const row = (label: string, measureKey: string, value: ReactNode, tone = 'text-(--color-ink)') => (
    <>
      <dt className="text-(--color-ink-3)">
        <Explain surface="statement" measureKey={measureKey}>{label}</Explain>
      </dt>
      <dd className={`m-0 text-right font-semibold tabular-nums ${tone}`}>{value}</dd>
    </>
  )
  const inHome = (n: number | null) => (
    <>
      {money(n)}
      {n !== null && home ? <span className="ml-1 text-(length:--text-micro) font-semibold text-(--color-ink-3)">{home}</span> : null}
    </>
  )
  return (
    <section aria-label="Account" className="min-w-0">
      <div className={`${LABEL} text-(--color-ink-3)`}>Account</div>
      <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-1.5 text-(length:--text-small)">
        {row('Customer account', 'customerAccount', h.customerAccount ?? DASH)}
        {row('Terms of payment', 'paymentTerms', h.paymentTerms ?? DASH)}
        {row('Currency', 'currency', currencies.join(' · '))}
        {row('Credit limit', 'creditLimit', inHome(h.creditLimit))}
        {row('Credit available', 'creditAvailable', inHome(h.creditAvailable), over ? 'text-(--color-overdue)' : 'text-(--color-ink)')}
      </dl>
      {use ? (
        <div className="mt-3" aria-label={use.over ? 'Over the credit limit' : `${Math.round(use.share * 100)}% of the credit limit used`}>
          <div className="h-1.5 overflow-hidden rounded-full bg-(--color-line)">
            <div
              className={`h-full rounded-full ${use.over ? 'bg-(--color-overdue)' : 'bg-(--color-navy-600)'}`}
              style={{ width: `${Math.max(2, use.share * 100)}%` }}
            />
          </div>
          <div className={`mt-1 text-(length:--text-micro) ${use.over ? 'font-semibold text-(--color-overdue)' : 'text-(--color-ink-3)'}`}>
            {use.over ? 'Over the credit limit' : `${Math.round(use.share * 100)}% of the limit used`}
          </div>
        </div>
      ) : null}
    </section>
  )
}

/** What the customer owes at the end: the figure the statement exists to state. */
function BalancePanel({ company: c, withheld }: { company: CompanyStatement; withheld: boolean }) {
  const h = c.header
  const [first, ...rest] = closingsOf(c)
  return (
    <section aria-label="Closing balance" className="min-w-0 rounded-lg bg-(--color-navy-800) px-5 py-4 text-white @max-5xl:col-span-2 @max-3xl:col-span-1">
      <div className={`${LABEL} text-(--color-navy-200)`}>
        <Explain surface="statement" measureKey="closing">Closing balance</Explain>
      </div>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-2">
        <span className="text-[1.875rem] leading-none font-bold tracking-[-0.02em] tabular-nums">
          {withheld ? DASH : money(first.balance)}
        </span>
        <span className="text-(length:--text-small) font-semibold text-(--color-navy-200)">{first.currency}</span>
      </div>
      {rest.map((r) => (
        <div key={r.currency} className="mt-2 flex items-baseline gap-x-2">
          <span className="text-[1.125rem] leading-none font-semibold tabular-nums">{withheld ? DASH : money(r.balance)}</span>
          <span className="text-(length:--text-micro) font-semibold text-(--color-navy-200)">{r.currency}</span>
        </div>
      ))}
      <div className="mt-3 border-t border-white/15 pt-2.5 text-(length:--text-small) text-(--color-navy-200)">
        {h.kind === 'open' ? openNote(h) : `As at ${docDate(h.toDate)}`}
      </div>
    </section>
  )
}

/** The ledger: per currency, the opening balance, every line, and the closing totals. */
function Ledger({ company: c, withheld }: { company: CompanyStatement; withheld: boolean }) {
  const h = c.header
  const open = h.kind === 'open'
  const columns = columnsFor(h.kind)
  const n = columns.length
  const firstFigure = columns.findIndex((col) => col.figure)
  const multi = c.sections.length > 1
  const cell = 'px-2.5 py-1.5 align-middle'
  return (
    /* NOTHING BREAKS AT A HYPHEN: dates, names, document and order numbers and
       figures stay on one line, and only a reference may wrap. A header may
       wrap at its space, so "Days overdue" never sets a column wider than its
       figures need. Below the sheet's minimum width the DESK scrolls sideways
       (CompanySheet), so the header still sticks; only on a phone does the table
       scroll inside the sheet. */
    <div className="scroll-slim @max-3xl:overflow-x-auto">
      <table className="report-table statement-doc w-full border-collapse text-(length:--text-small) @max-3xl:min-w-[60rem]">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={`sticky top-0 z-10 px-2.5 py-2 text-(length:--text-small) leading-tight font-semibold ${col.figure || col.key === 'overdueDays' ? 'num whitespace-normal!' : ''}`}
              >
                <Explain surface="statement" measureKey={col.key}>{col.header}</Explain>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {c.sections.length === 0 ? (
            <tr>
              <td colSpan={n} className="px-3 py-8 text-center! text-(--color-ink-3)">
                No transactions between {docDate(h.fromDate)} and {docDate(h.toDate)}, and nothing owed before it.
              </td>
            </tr>
          ) : null}
          {c.sections.map((s) => [
            multi ? (
              <tr key={`${s.currency}-ccy`} className="stmt-currency">
                <td colSpan={n} className="px-3 pt-4 pb-2">
                  <span className="rounded bg-(--color-navy-700) px-2 py-0.5 text-(length:--text-micro) font-bold tracking-[0.08em] text-white">
                    {s.currency}
                  </span>
                  <span className="ml-2 text-(length:--text-small) text-(--color-ink-3)">
                    <Explain surface="statement" measureKey="currency">Lines invoiced in {s.currency}</Explain>
                  </span>
                </td>
              </tr>
            ) : null,
            <tr key={`${s.currency}-open`} className="stmt-opening">
              <td colSpan={n - 1} className={cell}>
                <Explain surface="statement" measureKey="opening">Opening balance</Explain>
                <span className="ml-2 font-normal text-(--color-ink-3)">{openingNote(h)}</span>
              </td>
              <td className={`${cell} num`}>{money(s.opening?.balance ?? null)}</td>
            </tr>,
            ...s.lines.map((l) => (
              <tr key={`${s.currency}-${l.seq}`} className="stmt-line">
                <td className={`${cell} whitespace-nowrap`}>{docDate(l.lineDate)}</td>
                <td className={`${cell} whitespace-nowrap`}><bdi>{l.salesMan}</bdi></td>
                <td className={`${cell} font-medium whitespace-nowrap text-(--color-ink)`}>{l.document}</td>
                <td className={`${cell} whitespace-nowrap`}>{l.salesOrder}</td>
                <td className={`${cell} min-w-[7rem] break-words`}>{l.reference}</td>
                <td className={`${cell} whitespace-nowrap`}>{docDate(l.dueDate)}</td>
                <td className={`${cell} num`}><OverdueDays days={l.overdueDays} open={open} /></td>
                {open ? <td className={`${cell} num`}>{withheld ? DASH : money(l.originalAmount)}</td> : null}
                <td className={`${cell} num`}>{sideAmount(l.debit, withheld)}</td>
                <td className={`${cell} num`}>{sideAmount(l.credit, withheld)}</td>
                <td className={`${cell} num font-semibold text-(--color-ink)`}>{money(l.balance)}</td>
              </tr>
            )),
            <tr key={`${s.currency}-close`} className="stmt-closing">
              <td colSpan={firstFigure} className={`${cell} py-2.5`}>
                <Explain surface="statement" measureKey="closing">Closing balance</Explain>
                <span className="ml-2 font-normal text-(--color-ink-3)">
                  {closingNote(h)}
                </span>
              </td>
              {open ? <td className={`${cell} num`} /> : null}
              <td className={`${cell} num`}>{withheld ? DASH : money(s.closing?.debit ?? 0)}</td>
              <td className={`${cell} num`}>{withheld ? DASH : money(s.closing?.credit ?? 0)}</td>
              <td className={`${cell} num font-bold text-(--color-navy-800)`}>{money(s.closing?.balance ?? null)}</td>
            </tr>,
          ])}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Days overdue. On the Open statement every line is unpaid, so a line past its
 * due date is flagged; one not yet due is quiet. On the Full statement the
 * figure is printed as D365 prints it, on paid lines too, so it stays plain.
 */
function OverdueDays({ days, open }: { days: number | null; open: boolean }) {
  if (days === null) return null
  if (!open) return <span className="text-(--color-ink-3)">{days}</span>
  return days > 0 ? (
    <span className="rounded bg-(--color-overdue)/10 px-1.5 py-px font-semibold text-(--color-overdue)">{days}</span>
  ) : (
    <span className="text-(--color-ink-3)">{days}</span>
  )
}
